import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { useEffect, useMemo, useState } from "react";
import { getDeviceWellness, getLatestDeviceWellness, getWellnessHistory, getWellnessToday, saveWellnessToday } from "../api/endpoints";
import { Button, Card, EmptyState, ErrorMessage, Input, PageHeading, Select, Spinner, Stat, Textarea } from "../components/ui";
import { useAsync } from "../hooks/useAsync";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend);

function defaultFrom(): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - 30);
  return date.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function scoreOptions() {
  return ["", "1", "2", "3", "4", "5"];
}

type WellnessField = {
  label: string;
  value: string;
  setValue: (value: string) => void;
  testId: string;
};

export default function WellnessPage() {
  const [from, setFrom] = useState(defaultFrom());
  const [to, setTo] = useState(today());
  const { data, error, loading, reload } = useAsync(
    async () => {
      const [todayEntry, history, device, latestDevice] = await Promise.all([
        getWellnessToday(),
        getWellnessHistory(from, to),
        getDeviceWellness(from, to),
        getLatestDeviceWellness(),
      ]);
      return { todayEntry, history: history.entries, device: device.entries, latestDevice: latestDevice.entry };
    },
    [from, to],
  );

  const [sleepQuality, setSleepQuality] = useState("");
  const [muscleSoreness, setMuscleSoreness] = useState("");
  const [energyLevel, setEnergyLevel] = useState("");
  const [mood, setMood] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setSleepQuality(data.todayEntry.entry?.sleepQuality != null ? String(data.todayEntry.entry.sleepQuality) : "");
    setMuscleSoreness(data.todayEntry.entry?.muscleSoreness != null ? String(data.todayEntry.entry.muscleSoreness) : "");
    setEnergyLevel(data.todayEntry.entry?.energyLevel != null ? String(data.todayEntry.entry.energyLevel) : "");
    setMood(data.todayEntry.entry?.mood != null ? String(data.todayEntry.entry.mood) : "");
    setNotes(data.todayEntry.entry?.notes ?? "");
  }, [data]);

  const readinessChartData = useMemo(
    () => ({
      labels: data?.history.map((entry) => entry.entryDate) ?? [],
      datasets: [{ label: "Readiness", data: data?.history.map((entry) => entry.readinessScore ?? null) ?? [], borderColor: "#059669", backgroundColor: "rgba(5,150,105,0.15)" }],
    }),
    [data],
  );

  const deviceLineData = useMemo(
    () => ({
      labels: data?.device.map((entry) => entry.date) ?? [],
      datasets: [
        { label: "Body battery end", data: data?.device.map((entry) => entry.bodyBatteryEnd ?? null) ?? [], borderColor: "#059669", backgroundColor: "rgba(5,150,105,0.15)" },
        { label: "Resting HR", data: data?.device.map((entry) => entry.restingHeartRate ?? null) ?? [], borderColor: "#f59e0b", backgroundColor: "rgba(245,158,11,0.15)" },
      ],
    }),
    [data],
  );

  const sleepBarData = useMemo(
    () => ({
      labels: data?.device.map((entry) => entry.date) ?? [],
      datasets: [{ label: "Sleep minutes", data: data?.device.map((entry) => entry.sleepDurationMinutes ?? null) ?? [], backgroundColor: "rgba(5,150,105,0.7)" }],
    }),
    [data],
  );

  async function handleSave() {
    setBusy(true);
    setSaveError(null);
    try {
      await saveWellnessToday({
        sleepQuality: sleepQuality ? Number(sleepQuality) : null,
        muscleSoreness: muscleSoreness ? Number(muscleSoreness) : null,
        energyLevel: energyLevel ? Number(energyLevel) : null,
        mood: mood ? Number(mood) : null,
        notes: notes || null,
      });
      reload();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner />;
  if (error || !data) return <ErrorMessage error={error ?? new Error("Failed to load wellness")} onRetry={reload} />;

  const fields: WellnessField[] = [
    { label: "Sleep quality", value: sleepQuality, setValue: setSleepQuality, testId: "wellness-sleep-quality-input" },
    { label: "Muscle soreness", value: muscleSoreness, setValue: setMuscleSoreness, testId: "wellness-muscle-soreness-input" },
    { label: "Energy level", value: energyLevel, setValue: setEnergyLevel, testId: "wellness-energy-level-input" },
    { label: "Mood", value: mood, setValue: setMood, testId: "wellness-mood-input" },
  ];

  return (
    <div className="space-y-6" data-testid="wellness-page">
      <PageHeading title="Wellness dashboard" subtitle="Track today's self-report and long-term recovery trends." />

      <div className="grid gap-4 md:grid-cols-3">
        <Stat label="Today's readiness" value={data.todayEntry.readinessScore != null ? `${data.todayEntry.readinessScore}%` : "Not scored"} />
        <Stat label="Latest body battery" value={data.latestDevice?.bodyBatteryEnd != null ? `${data.latestDevice.bodyBatteryEnd}` : "No device data"} />
        <Stat label="Latest HRV" value={data.latestDevice?.hrvRmssdMs != null ? `${data.latestDevice.hrvRmssdMs} ms` : "No device data"} />
      </div>

      <Card className="p-6">
        <h2 className="text-heading-md text-gray-900 dark:text-gray-100">Today's self-report</h2>
        {saveError && <div className="mt-3"><ErrorMessage error={new Error(saveError)} /></div>}
        <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {fields.map((field) => (
            <label key={field.testId} className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
              <span>{field.label}</span>
              <Select value={field.value} onChange={(event) => field.setValue(event.target.value)} data-testid={field.testId}>
                <option value="">—</option>
                {scoreOptions().slice(1).map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Select>
            </label>
          ))}
        </div>
        <label className="mt-4 block space-y-1 text-sm text-gray-600 dark:text-gray-300">
          <span>Notes</span>
          <Textarea rows={4} value={notes} onChange={(event) => setNotes(event.target.value)} data-testid="wellness-notes-input" />
        </label>
        <div className="mt-4 flex justify-end">
          <Button onClick={() => void handleSave()} disabled={busy} data-testid="wellness-save-button">
            {busy ? "Saving…" : "Save today's entry"}
          </Button>
        </div>
      </Card>

      <Card className="p-4" data-testid="wellness-range-panel">
        <div className="grid gap-4 md:grid-cols-3">
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>From</span>
            <Input type="date" value={from} onChange={(event) => setFrom(event.target.value)} data-testid="wellness-from-input" />
          </label>
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>To</span>
            <Input type="date" value={to} onChange={(event) => setTo(event.target.value)} data-testid="wellness-to-input" />
          </label>
          <div className="flex items-end">
            <Button variant="secondary" onClick={reload} data-testid="wellness-range-refresh-button">Refresh</Button>
          </div>
        </div>
      </Card>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card className="p-6">
          <h2 className="text-heading-md text-gray-900 dark:text-gray-100">Self-reported wellness history</h2>
          {data.history.length === 0 ? (
            <div className="mt-4">
              <EmptyState title="No wellness history yet" description="Save today's entry to start building your readiness chart." testId="wellness-history-empty-state" />
            </div>
          ) : (
            <div className="mt-4" data-testid="wellness-history-chart-panel">
              <Line data={readinessChartData} />
            </div>
          )}
        </Card>

        <Card className="p-6">
          <h2 className="text-heading-md text-gray-900 dark:text-gray-100">Device recovery trends</h2>
          {data.device.length === 0 ? (
            <div className="mt-4">
              <EmptyState title="No device data yet" description="Garmin data will appear here once the sync starts storing rows." testId="wellness-device-empty-state" />
            </div>
          ) : (
            <div className="mt-4 space-y-6" data-testid="wellness-device-chart-panel">
              <Line data={deviceLineData} />
              <Bar data={sleepBarData} />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
